/**
 * PersonalDocuments — onboarding / personal document uploads per candidate.
 *
 * Renders on CandidateDetail below the timeline. Supports both link + file
 * upload per slot. Slots are read from GET /candidates/{cid}/personal-documents.
 */
import { useEffect, useRef, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
    IdCard, Fingerprint, Plane, Home, FileSignature, Landmark,
    Upload, Link as LinkIcon, Trash2, ExternalLink, CheckCircle2,
} from "lucide-react";

const SLOT_ICON = {
    pan: IdCard,
    aadhaar: Fingerprint,
    passport: Plane,
    address_proof: Home,
    offer_acceptance: FileSignature,
    bank_details: Landmark,
};

export default function PersonalDocuments({ candidateId }) {
    const [state, setState] = useState({ loading: true, docs: {}, slots: [], labels: {} });
    const [linkMode, setLinkMode] = useState({}); // slot -> {url, filename}
    const [uploading, setUploading] = useState({});
    const fileInputs = useRef({});

    const load = async () => {
        try {
            const { data } = await api.get(`/candidates/${candidateId}/personal-documents`);
            setState({ loading: false, docs: data.personal_documents || {}, slots: data.slots || [], labels: data.labels || {} });
        } catch (err) {
            toast.error(formatApiError(err));
            setState((s) => ({ ...s, loading: false }));
        }
    };
    useEffect(() => { if (candidateId) load(); }, [candidateId]);

    const addLink = async (slot) => {
        const meta = linkMode[slot] || {};
        const url = (meta.url || "").trim();
        if (!url) return;
        try {
            await api.post(`/candidates/${candidateId}/personal-documents/${slot}/link`, {
                url, filename: meta.filename || undefined,
            });
            toast.success(`${state.labels[slot]} linked`);
            setLinkMode((m) => ({ ...m, [slot]: null }));
            await load();
        } catch (err) { toast.error(formatApiError(err)); }
    };

    const uploadFile = async (slot, file) => {
        if (!file) return;
        setUploading((m) => ({ ...m, [slot]: true }));
        try {
            const fd = new FormData();
            fd.append("file", file);
            await api.post(`/candidates/${candidateId}/personal-documents/${slot}/upload`, fd, {
                headers: { "Content-Type": "multipart/form-data" },
            });
            toast.success(`${state.labels[slot]} uploaded`);
            await load();
        } catch (err) { toast.error(formatApiError(err)); }
        finally { setUploading((m) => ({ ...m, [slot]: false })); }
    };

    const removeSlot = async (slot) => {
        if (!confirm(`Remove ${state.labels[slot]}?`)) return;
        try {
            await api.delete(`/candidates/${candidateId}/personal-documents/${slot}`);
            toast.success("Removed");
            await load();
        } catch (err) { toast.error(formatApiError(err)); }
    };

    if (state.loading) return <div className="text-sm text-muted-foreground p-3">Loading personal docs…</div>;

    return (
        <div className="border border-border rounded p-4 space-y-3" data-testid="personal-documents">
            <div className="flex items-center justify-between gap-3">
                <div>
                    <div className="font-semibold text-lg">Personal / onboarding documents</div>
                    <div className="text-xs text-muted-foreground">
                        Government IDs, offer acceptance, bank details — needed before Day 1.
                    </div>
                </div>
                <div className="text-xs">
                    <span className="font-mono px-2 py-0.5 rounded-full bg-emerald-50 text-emerald-700 border border-emerald-200">
                        {Object.values(state.docs).filter(Boolean).length}/{state.slots.length} uploaded
                    </span>
                </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                {state.slots.map((slot) => {
                    const doc = state.docs[slot];
                    const Icon = SLOT_ICON[slot] || IdCard;
                    return (
                        <div
                            key={slot}
                            data-testid={`personal-doc-slot-${slot}`}
                            className={`border rounded p-3 ${doc ? "border-emerald-200 bg-emerald-50/40" : "border-border"}`}
                        >
                            <div className="flex items-center gap-2">
                                <Icon className={`h-4 w-4 ${doc ? "text-emerald-700" : "text-muted-foreground"}`} />
                                <div className="font-medium text-sm flex-1">{state.labels[slot]}</div>
                                {doc && <CheckCircle2 className="h-4 w-4 text-emerald-600" />}
                            </div>
                            {doc ? (
                                <div className="mt-2 text-xs flex items-center justify-between gap-2">
                                    <a
                                        href={doc.url}
                                        target="_blank"
                                        rel="noreferrer"
                                        className="text-ikb hover:underline inline-flex items-center gap-1 truncate"
                                        data-testid={`personal-doc-link-${slot}`}
                                    >
                                        {doc.filename || doc.url} <ExternalLink className="h-3 w-3 shrink-0" />
                                    </a>
                                    <button
                                        onClick={() => removeSlot(slot)}
                                        className="text-red-600 hover:text-red-800"
                                        data-testid={`personal-doc-remove-${slot}`}
                                    >
                                        <Trash2 className="h-3.5 w-3.5" />
                                    </button>
                                </div>
                            ) : linkMode[slot] ? (
                                <div className="mt-2 space-y-1">
                                    <Input
                                        placeholder="Paste URL"
                                        value={linkMode[slot]?.url || ""}
                                        onChange={(e) => setLinkMode((m) => ({ ...m, [slot]: { ...m[slot], url: e.target.value } }))}
                                        data-testid={`personal-doc-link-input-${slot}`}
                                        className="h-8 text-xs"
                                    />
                                    <div className="flex gap-1">
                                        <Button size="sm" className="h-7 text-xs" onClick={() => addLink(slot)} data-testid={`personal-doc-link-save-${slot}`}>
                                            Save link
                                        </Button>
                                        <Button size="sm" variant="ghost" className="h-7 text-xs"
                                                onClick={() => setLinkMode((m) => ({ ...m, [slot]: null }))}>
                                            Cancel
                                        </Button>
                                    </div>
                                </div>
                            ) : (
                                <div className="mt-2 flex gap-1">
                                    <Button
                                        size="sm" variant="outline" className="h-7 text-xs"
                                        onClick={() => fileInputs.current[slot]?.click()}
                                        disabled={uploading[slot]}
                                        data-testid={`personal-doc-upload-${slot}`}
                                    >
                                        <Upload className="h-3 w-3 mr-1" />
                                        {uploading[slot] ? "Uploading…" : "Upload"}
                                    </Button>
                                    <input
                                        type="file"
                                        ref={(el) => { fileInputs.current[slot] = el; }}
                                        onChange={(e) => uploadFile(slot, e.target.files?.[0])}
                                        className="hidden"
                                    />
                                    <Button
                                        size="sm" variant="ghost" className="h-7 text-xs"
                                        onClick={() => setLinkMode((m) => ({ ...m, [slot]: { url: "" } }))}
                                        data-testid={`personal-doc-linkbtn-${slot}`}
                                    >
                                        <LinkIcon className="h-3 w-3 mr-1" /> Link
                                    </Button>
                                </div>
                            )}
                        </div>
                    );
                })}
            </div>
        </div>
    );
}
